// Pagination.js
import React from 'react';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  // Calculate the range of pages to show
  const getVisiblePages = () => {
    const maxVisiblePages = 10;
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxVisiblePages) {
      // Calculate the start page based on current page
      const halfVisible = Math.floor(maxVisiblePages / 2);
      startPage = Math.max(1, currentPage - halfVisible);
      endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      // Adjust start page if we're near the end
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
    }

    return { startPage, endPage };
  };

  const { startPage, endPage } = getVisiblePages();
  const visiblePages = [];
  for (let i = startPage; i <= endPage; i++) {
    visiblePages.push(i);
  }

  return (
    <div className="pagination-container">
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          {/* First Page Button */}
          {totalPages > 10 && currentPage > 6 && (
            <>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  aria-label="First"
                >
                  <i className="fas fa-angle-double-left"></i> First
                </button>
              </li>
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            </>
          )}

          {/* Previous Button */}
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous"
            >
              <i className="fas fa-chevron-left"></i> Previous
            </button>
          </li>

          {/* Page Numbers */}
          {visiblePages.map((page) => (
            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            </li>
          ))}

          {/* Next Button */}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next"
            >
              Next <i className="fas fa-chevron-right"></i>
            </button>
          </li>

          {/* Last Page Button */}
          {totalPages > 10 && currentPage < totalPages - 5 && (
            <>
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last"
                >
                  Last <i className="fas fa-angle-double-right"></i>
                </button>
              </li>
            </>
          )}
        </ul>
      </nav>

      {/* Page Info */}
      <div className="pagination-info text-center mt-2">
        <small className="text-muted">
          Page {currentPage} of {totalPages} ({totalPages > 0 ? ((currentPage - 1) * 50 + 1) : 0} - {Math.min(currentPage * 50, totalPages * 50)} of {totalPages * 50} entries)
        </small>
      </div>

      <style jsx>{`
        .pagination-container {
          margin: 20px 0;
        }

        .pagination {
          margin-bottom: 0;
        }

        .page-link {
          color: #0AC949;
          border-color: #dee2e6;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          border-radius: 0.25rem;
          transition: all 0.3s ease;
        }

        .page-link:hover {
          color: #08b83f;
          background-color: #e9f7ef;
          border-color: #0AC949;
        }

        .page-item.active .page-link {
          background-color: #0AC949;
          border-color: #0AC949;
          color: white;
          font-weight: 600;
        }

        .page-item.disabled .page-link {
          color: #6c757d;
          background-color: #fff;
          border-color: #dee2e6;
          cursor: not-allowed;
        }

        .page-item.disabled .page-link:hover {
          color: #6c757d;
          background-color: #fff;
          border-color: #dee2e6;
        }

        .page-link i {
          font-size: 0.75rem;
        }

        .pagination-info {
          font-size: 0.8rem;
        }

        @media (max-width: 768px) {
          .pagination {
            flex-wrap: wrap;
            justify-content: center;
          }
          
          .page-item {
            margin: 2px;
          }
          
          .page-link {
            padding: 0.4rem 0.6rem;
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Pagination;
